import {
  SEVERITY_ORDER,
  type SeverityLabel,
} from "@/v2-backoffice-client/layer/drift/layer.drift.utils"
import { hasAnyDrift } from "@/v2-backoffice-client/uptime/uptime.buckets.utils"
import type { LayerDriftSummaryFragment } from "@/v2-backoffice-client/uptime/dashboard/__types__/dashboard.driftSummaries.fragment"
import type { UptimeInlineFragment } from "@/v2-backoffice-client/uptime/inline/__types__/uptime.inline.fragment"
import type { UptimeOverviewFragment } from "@/v2-backoffice-client/uptime/__types__/uptime.overview.fragment"
import type {
  DashboardDriftByKey,
  DashboardOverallStatus,
  DashboardTargetSplit,
} from "@/v2-backoffice-client/uptime/dashboard/uptime.dashboard.types"

// Stable key for matching an UptimeTarget to its drift summary.
// `Source|Identifier` is unique per target (the API guarantees it as
// the natural primary key).
export function dashboardTargetKey(t: UptimeInlineFragment): string {
  return `${t.Source}|${t.Identifier}`
}

// Bucket the visible targets into Down / Slow / Drift lanes and
// sort each lane by "what should the room look at first".
//
//   - Muted targets are dropped entirely. The wall-display dashboard
//     is "what should we panic about right now?" and the operator
//     already explicitly said "not this".
//   - Down + drift are not mutually exclusive: a drifting target
//     that is also down lands in both lanes.
//   - Down ordering: freshest-down first (the new emergency wins).
//   - Slow ordering: biggest deviation from baseline first (because
//     a service that's 10x its baseline beats one that's 1.2x even
//     if the absolute number is similar).
//   - Drift ordering: highest severity first (breaking > warning >
//     info), tiebroken by detection recency. A breaking change
//     always sits above an info-only change so the lane reads
//     "panic stuff first".
export function splitTargets(
  targets: UptimeInlineFragment[],
  driftByKey: DashboardDriftByKey,
): DashboardTargetSplit {
  const down: UptimeInlineFragment[] = []
  const slow: UptimeInlineFragment[] = []
  const drift: UptimeInlineFragment[] = []
  for (const t of targets) {
    if (t.IsMuted) continue
    if (t.LatestStatus === "down") down.push(t)
    else if (t.IsSlowOutlier) slow.push(t)
    if (hasAnyDrift(t)) drift.push(t)
  }
  down.sort((a, b) => ts(b.CurrentStatusSince) - ts(a.CurrentStatusSince))
  slow.sort((a, b) => latRatio(b) - latRatio(a))
  drift.sort((a, b) => {
    const sa = severityRank(driftByKey.get(dashboardTargetKey(a)))
    const sb = severityRank(driftByKey.get(dashboardTargetKey(b)))
    if (sa !== sb) return sb - sa
    // Tiebreak with the most recent of the two drift timestamps so a
    // count drift detected after the metadata drift surfaces above
    // an older metadata-only entry.
    const aDetected = Math.max(
      ts(a.DriftDetectedAt),
      ts(a.CountDriftDetectedAt),
    )
    const bDetected = Math.max(
      ts(b.DriftDetectedAt),
      ts(b.CountDriftDetectedAt),
    )
    return bDetected - aDetected
  })
  return { down, slow, drift }
}

// Higher = worse. Used to sort the drift lane.
//
// Folds in the count-drift severity so a target whose only drift is
// "feature count dropped 80%" still ranks alongside metadata drift
// of the same tier instead of sorting to the bottom of the lane.
export function severityRank(s: LayerDriftSummaryFragment | undefined): number {
  if (!s) return 0
  let rank = 0
  if (s.BreakingCount > 0) rank = Math.max(rank, 3)
  else if (s.WarningCount > 0) rank = Math.max(rank, 2)
  else if (s.InfoCount > 0) rank = Math.max(rank, 1)
  const cd = s.CountDrift
  if (cd?.HasCountDrift) {
    if (cd.Severity === "breaking") rank = Math.max(rank, 3)
    else if (cd.Severity === "warning") rank = Math.max(rank, 2)
    else if (cd.Severity === "info") rank = Math.max(rank, 1)
  }
  return rank
}

// Latency ratio: current / baseline. Used to rank the slow lane so
// the worst-deviating service surfaces first.
export function latRatio(t: UptimeInlineFragment): number {
  const c = t.AvgDurationMs24h ?? t.LastDurationMs ?? 0
  const b = t.P95DurationMs90d ?? t.AvgDurationMs90d ?? 0
  if (b <= 0) return 0
  return c / b
}

// ISO-string parse to epoch-ms with a 0 fallback for null/undefined
// inputs. Used everywhere we need to sort by recency without
// special-casing missing timestamps.
export function ts(iso: string | null | undefined): number {
  return iso ? new Date(iso).getTime() : 0
}

// Map a fleet uptime % to a KPI tile tone.
//   >= 99.9% -> ok       (the SLO band)
//   >= 99%   -> warning  (drifting toward trouble)
//   <  99%   -> critical (operators need to look)
export function uptimeTone(pct: number | null): "ok" | "warning" | "critical" {
  if (pct == null) return "ok"
  if (pct >= 99.9) return "ok"
  if (pct >= 99) return "warning"
  return "critical"
}

// Aggregate severity totals across every drifting summary so the
// Configuration-drift KPI tile can show a "X breaking · Y warning ·
// Z info" sub-line at a glance instead of a generic blurb.
export function sumDriftSeverity(
  driftByKey: DashboardDriftByKey,
): Record<SeverityLabel, number> {
  const totals: Record<SeverityLabel, number> = {
    breaking: 0,
    warning: 0,
    info: 0,
  }
  for (const s of driftByKey.values()) {
    totals.breaking += s.BreakingCount
    totals.warning += s.WarningCount
    totals.info += s.InfoCount
  }
  return totals
}

// Render the severity totals as a "N breaking · N warning · N info"
// string. Returns an empty string when nothing drifts so callers can
// fall back to a generic blurb.
export function formatDriftSeveritySummary(
  totals: Record<SeverityLabel, number>,
): string {
  const parts: string[] = []
  for (const sev of SEVERITY_ORDER) {
    if (totals[sev] > 0) parts.push(`${totals[sev]} ${sev}`)
  }
  return parts.join(" · ")
}

// Compute the page-level status used for the brand dot, the giant
// headline copy, and the page-edge glow. Mute-aware by virtue of
// the backend: every count on `overview` already excludes silenced
// targets (see UptimeOverviewFragment).
export function computeOverallStatus(
  overview: UptimeOverviewFragment | null,
  driftCount: number,
): DashboardOverallStatus {
  if (!overview) {
    return {
      level: "ok",
      headline: "Loading…",
      dotVariant: "success-1",
    }
  }
  if (overview.DownCount > 0) {
    const n = overview.DownCount
    return {
      level: "critical",
      headline: `${n} service${n === 1 ? "" : "s"} down`,
      dotVariant: "destructive",
    }
  }
  if (overview.SkippedCount > 0 || driftCount > 0) {
    const parts: string[] = []
    if (overview.SkippedCount > 0)
      parts.push(`${overview.SkippedCount} skipped`)
    if (driftCount > 0) parts.push(`${driftCount} drifting`)
    return {
      level: "warning",
      headline: parts.join(" · "),
      dotVariant: "warning",
    }
  }
  return {
    level: "ok",
    headline: "All systems operational",
    dotVariant: "success-1",
  }
}
